import React, { useState, useEffect, useCallback } from 'react';
import { foundationAPI, jobApiError } from '../services/api';
import { FiAlertCircle, FiCheckCircle, FiPrinter } from 'react-icons/fi';

// Foundation step 4 (brainstorms/2026-10-17-foundation-enforcement.md): values-first hiring
// (Part 5) and the quarterly review pack (Part 7).

const muted = { color: '#64748b', fontSize: 12 };
const row = { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 6, marginTop: 6 };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const Err = ({ text }) => (text ? <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}><FiAlertCircle size={16} />{text}</div> : null);
const Ok = ({ text }) => (text ? <div style={{ color: '#15803d', fontSize: 13, margin: '8px 0', display: 'flex', gap: 6, alignItems: 'center' }}><FiCheckCircle />{text}</div> : null);
const SCALE = [[1, '1 Red flag'], [2, '2 Vague'], [3, '3 Clear example'], [4, '4 Strong']];
const COLOUR = { GREEN: ['#15803d', 'G'], AMBER: ['#b45309', 'A'], RED: ['#b91c1c', 'R'], GREY: ['#94a3b8', '–'] };

const useRun = (after) => {
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const run = async (fn, done) => {
    try { const r = await fn(); setErr(''); setOk(done || ''); if (after) after(r); return r; } catch (e) { setOk(''); setErr(jobApiError(e)); return null; }
  };
  return { err, ok, run };
};

// ─── Values scoring form (used at the interview and the day-90 review) ─────
const ValuesForm = ({ values, initial, onSubmit, label }) => {
  const [s, setS] = useState(() => Object.fromEntries(values.map(([v]) => [v, { score: initial?.[v] || '', note: '' }])));
  const done = values.every(([v]) => s[v].score);
  return (
    <div>
      {values.map(([v, l]) => (
        <div key={v} style={{ display: 'grid', gridTemplateColumns: '180px 150px 1fr', gap: 6, marginTop: 4 }}>
          <span>{l}</span>
          <select value={s[v].score} onChange={(e) => setS({ ...s, [v]: { ...s[v], score: e.target.value } })}>
            <option value="">Score…</option>{SCALE.map(([n, t]) => <option key={n} value={n}>{t}</option>)}
          </select>
          <input placeholder="Notes: what they said" value={s[v].note} onChange={(e) => setS({ ...s, [v]: { ...s[v], note: e.target.value } })} />
        </div>
      ))}
      <div style={{ textAlign: 'right', marginTop: 6 }}>
        <button className="btn-primary" disabled={!done} onClick={() => onSubmit(values.map(([v]) => ({ value: v, score: Number(s[v].score), note: s[v].note })))}>{label}</button>
      </div>
    </div>
  );
};

// ─── One candidate, stage by stage ─────────────────────────────────────────
const CandidatePanel = ({ id, meta, people, onClose, onChanged }) => {
  const [c, setC] = useState(null);
  const [f, setF] = useState({});
  const { err, ok, run } = useRun((r) => { if (r?.data?.id) { setC(r.data); onChanged(); } });
  const load = useCallback(() => foundationAPI.candidate(id).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return null;
  const act = (op, body, done) => run(() => foundationAPI.candidateAction(c.id, op, body), done);
  const set = (k, v) => setF({ ...f, [k]: v });
  const closed = ['HIRED', 'REJECTED', 'WITHDRAWN'].includes(c.stage);
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>{c.name} · {c.vacancy_title} · {c.stage_label}</span><button className="btn-secondary" onClick={onClose}>Close</button></div>
      <div style={muted}>{c.phone} {c.email} · {c.source_label} · applied {fmtDate(c.applied_on)} · reply {c.replied_on ? `sent ${fmtDate(c.replied_on)}` : `due ${fmtDate(c.reply_due)}`}{c.reply_overdue ? ' (late)' : ''}</div>
      {c.closed_reason && <div><strong>Outcome:</strong> {c.closed_reason}</div>}
      <Err text={err} /><Ok text={ok} />
      {c.is_runner && !c.replied_on && <button className="btn-secondary" onClick={() => act('replied', {}, 'Reply recorded.')}>Reply sent today</button>}

      {c.stage === 'SCREEN' && c.is_runner && (
        <div style={{ marginTop: 8 }}>
          <strong>Stage 3 · Screening (the basics only)</strong>
          <div style={grid}>
            {[['right_to_work', 'Right to work in the location'], ['languages_ok', 'Required language(s)'], ['experience_ok', 'Minimum experience or qualification'], ['application_complete', 'Complete application']].map(([k, l]) => (
              <label key={k} style={muted}>{l}
                <select value={f[k] ?? ''} onChange={(e) => set(k, e.target.value === '' ? undefined : e.target.value === 'yes')}>
                  <option value="">…</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </label>
            ))}
          </div>
          <input style={{ width: '100%', marginTop: 6 }} placeholder="Note (optional)" value={f.note || ''} onChange={(e) => set('note', e.target.value)} />
          <div style={{ textAlign: 'right', marginTop: 6 }}><button className="btn-primary" onClick={() => act('screen', f, 'Screened.')}>Save screening</button></div>
        </div>
      )}

      {c.stage === 'VALUES' && (
        <div style={{ marginTop: 8 }}>
          <strong>Stage 4 · Values interview (the gate)</strong>
          <div style={muted}>Same questions for every candidate. Each panel member scores on their own first; then the CEO or COO records the agreed scores. Pass = every value 3 or more.</div>
          <div style={muted}>Panel: {c.panel.map((p) => p.name).join(', ') || 'not set'} · {c.scorers_done} of 2 have scored</div>
          {c.is_runner && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <select multiple size={4} value={f.panel || c.panel.map((p) => String(p.id))} onChange={(e) => set('panel', [...e.target.selectedOptions].map((o) => o.value))}>
                {people.filter((p) => p.role !== 'CSO').map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
              </select>
              <button className="btn-secondary" onClick={() => act('panel', { panel: (f.panel || []).map(Number) }, 'Panel set.')}>Set panel (CEO or COO + one other)</button>
            </div>
          )}
          {c.on_panel && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600 }}>Your own scores</div>
              <ValuesForm key={`mine-${c.id}`} values={meta.values} initial={Object.fromEntries(c.values.map((v) => [v.value, v.my_score]))} label="Save my scores"
                onSubmit={(scores) => act('values', { scores }, 'Your scores are saved.')} />
            </div>
          )}
          {c.on_panel && ['CEO', 'COO'].includes(meta.myRole) && c.scorers_done >= 2 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600 }}>Agreed scores</div>
              <div style={{ display: 'flex', gap: 12, ...muted }}>
                <label><input type="checkbox" checked={!!f.arrived_on_time} onChange={(e) => set('arrived_on_time', e.target.checked)} /> Arrived on time</label>
                <label><input type="checkbox" checked={!!f.let_others_finish} onChange={(e) => set('let_others_finish', e.target.checked)} /> Let others finish speaking</label>
              </div>
              <ValuesForm key={`agreed-${c.id}`} values={meta.values} label="Record agreed scores"
                onSubmit={(scores) => act('agree-values', { scores, arrived_on_time: !!f.arrived_on_time, let_others_finish: !!f.let_others_finish }, 'Agreed scores recorded.')} />
            </div>
          )}
        </div>
      )}

      {c.values.some((v) => v.agreed) && c.stage !== 'VALUES' && (
        <div style={muted}>Values (agreed): {c.values.map((v) => `${v.label} ${v.agreed || '—'}`).join(' · ')}{c.arrived_on_time === false ? ' · arrived late' : ''}{c.let_others_finish === false ? ' · interrupted' : ''}</div>
      )}

      {c.stage === 'SKILLS' && (
        <div style={{ marginTop: 8 }}>
          <strong>Stage 5 · Role and skills assessment</strong>
          <input style={{ width: '100%', marginTop: 6 }} placeholder="Practical task given" value={f.practical_task || ''} onChange={(e) => set('practical_task', e.target.value)} />
          <input style={{ width: '100%', marginTop: 6 }} placeholder="How they did" value={f.practical_note || ''} onChange={(e) => set('practical_note', e.target.value)} />
          {c.role_sheet.map((i) => (
            <div key={i.item_id} style={{ ...row, display: 'grid', gridTemplateColumns: '1fr 60px 140px', gap: 6 }}>
              <span>{i.text}</span><span style={muted}>Exp. {i.expected}</span>
              <select value={f[`s${i.item_id}`] || ''} onChange={(e) => set(`s${i.item_id}`, e.target.value)}>
                <option value="">Score…</option>{[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          ))}
          <div style={{ textAlign: 'right', marginTop: 6 }}>
            <button className="btn-primary" disabled={c.role_sheet.some((i) => !f[`s${i.item_id}`])}
              onClick={() => act('skills', { practical_task: f.practical_task, practical_note: f.practical_note, scores: c.role_sheet.map((i) => ({ item_id: i.item_id, score: Number(f[`s${i.item_id}`]) })) }, 'Skills scored.')}>Save skills scores</button>
          </div>
        </div>
      )}

      {c.skills.length > 0 && (
        <div style={muted}>Gaps: {c.skills.filter((s) => s.gap > 0).map((s) => `${s.text} (${s.gap})`).join(' · ') || 'none'}{c.practical_note ? ` · Practical: ${c.practical_note}` : ''}</div>
      )}

      {c.stage === 'GAP' && c.is_runner && (
        <div style={{ marginTop: 8 }}>
          <strong>Stage 6 · Gap review and training plan</strong>
          <div style={muted}>Gaps of 1 go into the training plan. Gaps of 2 or more: can training close them within the 3-month probation?</div>
          {c.skills.some((s) => s.gap >= 2) && (
            <select value={f.trainable ?? ''} onChange={(e) => set('trainable', e.target.value === '' ? undefined : e.target.value === 'yes')}>
              <option value="">Can training close the big gaps in probation?</option><option value="yes">Yes, hire with a training plan</option><option value="no">No, not for this role</option>
            </select>
          )}
          <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="Training plan for the onboarding" value={f.training_plan || ''} onChange={(e) => set('training_plan', e.target.value)} />
          <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="Reasons for the decision" value={f.reasons || ''} onChange={(e) => set('reasons', e.target.value)} />
          <div style={{ textAlign: 'right' }}><button className="btn-primary" onClick={() => act('gap', { trainable: f.trainable, training_plan: f.training_plan, reasons: f.reasons }, 'Decision recorded.')}>Record decision</button></div>
        </div>
      )}

      {c.stage === 'CHECKS' && c.is_runner && (
        <div style={{ marginTop: 8 }}>
          <strong>Stage 7 · References and checks</strong>
          <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="Reference 1: who, and what they said about the values and skills" defaultValue={c.checks.reference_1} onChange={(e) => set('reference_1', e.target.value)} />
          <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="Reference 2" defaultValue={c.checks.reference_2} onChange={(e) => set('reference_2', e.target.value)} />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 13 }}>
            {[['reference_recent_manager', 'One reference is a recent manager'], ['id_verified', 'ID verified'], ['right_to_work_verified', 'Right to work verified'], ['qualifications_verified', 'Qualifications verified'],
              ...(c.needs_police_clearance ? [['police_clearance', 'Police clearance (sensitive role)']] : [])].map(([k, l]) => (
              <label key={k}><input type="checkbox" defaultChecked={c.checks[k]} onChange={(e) => set(k, e.target.checked)} /> {l}</label>
            ))}
          </div>
          <input style={{ width: '100%', marginTop: 6 }} placeholder="Conflict of interest declared (team rule 10.1): who, and they took no part" defaultValue={c.checks.conflict_declared} onChange={(e) => set('conflict_declared', e.target.value)} />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn-secondary" onClick={() => act('checks', f, 'Saved.')}>Save</button>
            <button className="btn-primary" onClick={() => act('clear-checks', f, 'Checks clear. The CEO makes the offer.')}>Checks clear</button>
          </div>
        </div>
      )}

      {c.stage === 'OFFER' && c.is_ceo && (
        <div style={{ marginTop: 8 }}>
          <strong>Stage 8 · Offer and sign-up</strong>
          <div style={muted}>Create their VMS account first, and record the signed contract and confidentiality agreement under Contracts & agreements. They acknowledge the team rules when they first sign in.</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <select value={f.user || ''} onChange={(e) => set('user', e.target.value)}>
              <option value="">Their VMS account…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            </select>
            <label style={muted}>Start date <input type="date" value={f.start_date || ''} onChange={(e) => set('start_date', e.target.value)} /></label>
            <button className="btn-primary" disabled={!f.user || !f.start_date} onClick={() => act('hire', { user: f.user, start_date: f.start_date }, 'Hired. Probation has started.')}>Hire</button>
          </div>
        </div>
      )}
      {c.stage === 'HIRED' && <div>Hired as {c.hired_user}, starting {fmtDate(c.start_date)}.</div>}

      {!closed && c.is_runner && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <input style={{ flex: 1 }} placeholder="Reason (the applicant still gets a reply)" value={f.reason || ''} onChange={(e) => set('reason', e.target.value)} />
          <button className="btn-secondary" disabled={!(f.reason || '').trim()} onClick={() => act('reject', { reason: f.reason }, 'Closed.')}>Not hired</button>
          <button className="btn-secondary" disabled={!(f.reason || '').trim()} onClick={() => act('withdraw', { reason: f.reason }, 'Closed.')}>Withdrew</button>
        </div>
      )}
    </div>
  );
};

const VacancyCard = ({ v, data, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', phone: '', email: '', source: 'SOCIAL', applied_on: today() });
  const { err, ok, run } = useRun(onChanged);
  const act = (op, body, done) => run(() => foundationAPI.vacancyAction(v.id, op, body), done);
  const counts = Object.entries(v.candidates_by_stage).filter(([, n]) => n).map(([s, n]) => `${n} ${s.toLowerCase()}`).join(', ');
  return (
    <div style={row}>
      <div style={{ display: 'flex', gap: 8, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <strong style={{ flex: 1 }}>{v.title} · {v.role}{v.location ? ` · ${v.location}` : ''}</strong>
        <span style={muted}>{counts || 'no applicants'}</span>
        <span className="status-pill">{v.status_label}</span>
      </div>
      {open && (
        <div style={{ marginTop: 6 }}>
          <Err text={err} /><Ok text={ok} />
          <div><strong>Why:</strong> {v.reason_label}. {v.reason_note}</div>
          <div style={muted}>{v.hours_label}{v.pay_range ? ` · ${v.pay_range}` : ''}{v.sensitive ? ' · police clearance required' : ''} · reports to {v.profile.reports_to || '—'} · assessed by {v.profile.assessor || '—'}</div>
          {v.duties && <div><strong>Duties (RACI):</strong> {v.duties}</div>}
          <div style={muted}>KPIs: {v.profile.kpis.map((k) => `${k.code} ${k.name}`).join(' · ') || '—'}</div>
          <div style={muted}>Role sheet: {v.profile.role_sheet.length} items, from the gap analysis.</div>
          <div style={muted}>Budget: {v.budget_confirmed_at ? `confirmed by ${v.budget_confirmed_by}` : 'waiting for the CFO'} · Profile: {v.profile_signed_at ? `signed off by ${v.profile_signed_by}` : 'waiting for the CEO'}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {v.can_confirm_budget && <button className="btn-secondary" onClick={() => act('budget', {}, 'Budget confirmed.')}>Confirm budget (CFO)</button>}
            {v.can_sign_profile && <button className="btn-secondary" onClick={() => act('sign', {}, 'Profile signed off.')}>Sign off profile (CEO)</button>}
            {v.status === 'DRAFT' && v.budget_confirmed_at && v.profile_signed_at && <button className="btn-primary" onClick={() => act('advertise', {}, 'Advertised. Name the six values first in the advert.')}>Mark as advertised</button>}
            {data.is_runner && ['DRAFT', 'OPEN'].includes(v.status) && <button className="btn-secondary" onClick={() => window.confirm('Cancel this vacancy?') && act('cancel', {}, 'Cancelled.')}>Cancel vacancy</button>}
          </div>
          {v.status === 'OPEN' && (
            <div style={{ marginTop: 8 }}>
              <strong>Add an applicant</strong>
              <div style={grid}>
                <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>{data.sources.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                <label style={muted}>Applied <input type="date" value={f.applied_on} max={today()} onChange={(e) => setF({ ...f, applied_on: e.target.value })} /></label>
              </div>
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <button className="btn-secondary" disabled={!f.name.trim() || !(f.phone || f.email)} onClick={() => act('candidates', f, 'Added. They must get a reply within 10 working days.').then((r) => r && setF({ ...f, name: '', phone: '', email: '' }))}>Add applicant</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProbationCard = ({ p, values, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ note: '', until: '', reason: '' });
  const { err, ok, run } = useRun(onChanged);
  const act = (op, body, done) => run(() => foundationAPI.probationAction(p.id, op, body), done);
  return (
    <div style={row}>
      <div style={{ display: 'flex', gap: 8, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <strong style={{ flex: 1 }}>{p.person} · {p.role}</strong>
        <span style={muted}>started {fmtDate(p.starts_on)} · review {fmtDate(p.review_due)}</span>
        <span className="status-pill">{p.outcome_label || 'On probation'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 6 }}>
          <Err text={err} /><Ok text={ok} />
          {p.training_plan && <div><strong>Training plan:</strong> {p.training_plan}</div>}
          <div>Day 30 ({fmtDate(p.day30)}): {p.day30_note || <span style={muted}>not recorded</span>}</div>
          <div>Day 60 ({fmtDate(p.day60)}): {p.day60_note || <span style={muted}>not recorded</span>}</div>
          {p.extended_to && <div>Extended to {fmtDate(p.extended_to)}</div>}
          {p.review_note && <div><strong>Review:</strong> {p.review_note}</div>}
          {!p.outcome && (
            <>
              <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="Day 30: training plan progress, first KPI data · Day 60: gap scores re-checked · Day 90: values, KPIs, gap progress" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {!p.day30_note && <button className="btn-secondary" onClick={() => act('check-in', { day: 30, note: f.note }, 'Day 30 recorded.')}>Record day 30</button>}
                {p.day30_note && !p.day60_note && <button className="btn-secondary" onClick={() => act('check-in', { day: 60, note: f.note }, 'Day 60 recorded.')}>Record day 60</button>}
                <button className="btn-secondary" onClick={() => window.confirm('End this probation? Use this only after training and a documented consult.') && act('decide', { outcome: 'ENDED', note: f.note }, 'Recorded.')}>End probation</button>
              </div>
              {!p.extended_to && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  <label style={muted}>Extend once, to <input type="date" value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} /></label>
                  <input style={{ flex: 1 }} placeholder="What progress is real but not complete" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
                  <button className="btn-secondary" onClick={() => act('extend', { until: f.until, reason: f.reason }, 'Extended.')}>Extend</button>
                </div>
              )}
              <div style={{ marginTop: 8, fontWeight: 600 }}>Pass the probation: re-score the values</div>
              <ValuesForm values={values} label="Passed" onSubmit={(scores) => act('decide', { outcome: 'PASSED', note: f.note, values: scores }, 'Probation passed.')} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const HiringTab = ({ people, myRole }) => {
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  useEffect(() => { foundationAPI.roles().then((r) => setRoles(r.data.roles || r.data)).catch(() => {}); }, []);
  const [openId, setOpenId] = useState(null);
  const [v, setV] = useState({ role: '', title: '', location: '', hours: 'FULL', pay_range: '', reason: '', reason_note: '', duties: '', sensitive: false });
  const [x, setX] = useState({ user: '', left_on: today(), reason: 'RESIGNED', why_left: '', what_would_have_kept: '', interview_held: true });
  const load = useCallback(() => foundationAPI.hiring().then((r) => setData(r.data)), []);
  const { err, ok, run } = useRun(load);
  useEffect(() => { load().catch(() => {}); }, [load]);
  if (!data) return <Err text={err} />;
  const meta = { values: data.values, myRole };
  return (
    <div>
      <div style={{ ...muted, marginBottom: 8 }}>We hire first on values, then on role and skills, then we look at the gaps. Values cannot be trained, so they are a gate; skills can be trained, so gaps are planned for.</div>
      <Err text={err} /><Ok text={ok} />
      {openId && <CandidatePanel key={openId} id={openId} meta={meta} people={people} onClose={() => setOpenId(null)} onChanged={load} />}
      {data.candidates.length > 0 && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Applicants</span></div>
          {data.candidates.map((c) => (
            <div key={c.id} style={{ ...row, display: 'flex', gap: 8, cursor: 'pointer' }} onClick={() => setOpenId(c.id)}>
              <strong style={{ flex: 1 }}>{c.name} · {c.vacancy_title}</strong>
              {c.reply_overdue && <span style={{ color: '#b91c1c', fontSize: 12 }}>reply overdue</span>}
              <span className="status-pill">{c.stage_label}</span>
            </div>
          ))}
        </div>
      )}
      {(data.vacancies.length > 0 || data.is_runner) && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Vacancies</span></div>
          {data.vacancies.length === 0 && <div className="cv-subsection-empty">None.</div>}
          {data.vacancies.map((vac) => <VacancyCard key={vac.id} v={vac} data={data} onChanged={load} />)}
          {data.is_runner && (
            <div style={{ marginTop: 10 }}>
              <strong>Open a vacancy (stage 0 and 1)</strong>
              <div style={grid}>
                <select value={v.role} onChange={(e) => setV({ ...v, role: e.target.value })}><option value="">Role…</option>{roles.map((r) => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}</select>
                <input placeholder="Title (optional)" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} />
                <input placeholder="Location" value={v.location} onChange={(e) => setV({ ...v, location: e.target.value })} />
                <select value={v.hours} onChange={(e) => setV({ ...v, hours: e.target.value })}><option value="FULL">Full-time</option><option value="PART">Part-time</option></select>
                <input placeholder="Pay range" value={v.pay_range} onChange={(e) => setV({ ...v, pay_range: e.target.value })} />
              </div>
              <select style={{ width: '100%', marginTop: 6 }} value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })}><option value="">Why is the role needed?</option>{data.reasons.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="The reason, written down" value={v.reason_note} onChange={(e) => setV({ ...v, reason_note: e.target.value })} />
              <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="What the role does: its RACI rows" value={v.duties} onChange={(e) => setV({ ...v, duties: e.target.value })} />
              <label style={{ fontSize: 13 }}><input type="checkbox" checked={v.sensitive} onChange={(e) => setV({ ...v, sensitive: e.target.checked })} /> Handles passports, medical records or money, or works with young students (police clearance)</label>
              <div style={{ textAlign: 'right', marginTop: 6 }}><button className="btn-primary" disabled={!v.role || !v.reason || v.reason_note.trim().length < 10} onClick={() => run(() => foundationAPI.openVacancy(v), 'Vacancy opened. The CFO and CEO have been asked to sign off.')}>Open vacancy</button></div>
            </div>
          )}
        </div>
      )}
      {data.is_runner && (
        <>
          <div className="cv-subsection">
            <div className="cv-subsection-title"><span>Probation (3 months)</span></div>
            {data.probations.length === 0 && <div className="cv-subsection-empty">Nobody on probation.</div>}
            {data.probations.map((p) => <ProbationCard key={p.id} p={p} values={data.values} onChanged={load} />)}
          </div>
          <div className="cv-subsection">
            <div className="cv-subsection-title"><span>Leavers and exit interviews</span></div>
            {data.exits.map((e) => (
              <div key={e.id} style={row}><strong>{e.person}</strong> · left {fmtDate(e.left_on)} · {e.reason}{e.why_left ? ` · why: ${e.why_left}` : ''}{e.what_would_have_kept ? ` · would have kept them: ${e.what_would_have_kept}` : ''}</div>
            ))}
            <div style={grid}>
              <select value={x.user} onChange={(e) => setX({ ...x, user: e.target.value })}><option value="">Who left…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}</select>
              <input type="date" value={x.left_on} onChange={(e) => setX({ ...x, left_on: e.target.value })} />
              <select value={x.reason} onChange={(e) => setX({ ...x, reason: e.target.value })}>{data.exit_reasons.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            </div>
            <input style={{ width: '100%', marginTop: 6 }} placeholder="Exit interview: why they left" value={x.why_left} onChange={(e) => setX({ ...x, why_left: e.target.value })} />
            <input style={{ width: '100%', marginTop: 6 }} placeholder="What would have kept them" value={x.what_would_have_kept} onChange={(e) => setX({ ...x, what_would_have_kept: e.target.value })} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <label style={{ fontSize: 13 }}><input type="checkbox" checked={x.interview_held} onChange={(e) => setX({ ...x, interview_held: e.target.checked })} /> Exit interview held</label>
              <button className="btn-secondary" disabled={!x.user} onClick={() => window.confirm('Record this leaver? Their roles end on the leaving date.') && run(() => foundationAPI.recordExit(x), 'Recorded. Their roles have ended.')}>Record leaver</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Quarterly review ──────────────────────────────────────────────────────
const Pills = ({ colours }) => colours.map((c, i) => {
  const [hex, t] = COLOUR[c] || COLOUR.GREY;
  return <span key={i} title={c} style={{ display: 'inline-block', width: 22, textAlign: 'center', color: '#fff', background: hex, borderRadius: 4, marginRight: 2, fontSize: 11 }}>{t}</span>;
});

const Counts = ({ obj }) => Object.entries(obj || {}).map(([k, n]) => `${k}: ${n}`).join(' · ');

const Pack = ({ id, people, documents }) => {
  const [p, setP] = useState(null);
  const [inputs, setInputs] = useState({});
  const [prop, setProp] = useState({ document: 'RULES', proposal: '' });
  const [close, setClose] = useState({ held_on: today(), priorities: [{ text: '', owner: '' }, { text: '', owner: '' }, { text: '', owner: '' }] });
  const apply = (d) => { setP(d); setInputs(d.inputs); };
  const { err, ok, run } = useRun((r) => r?.data?.id && apply(r.data));
  useEffect(() => { foundationAPI.review(id).then((r) => apply(r.data)); }, [id]);
  if (!p) return null;
  const k = p.kpis;
  const field = (name, label) => (
    <div style={{ marginTop: 8 }}>
      <strong>{label}</strong>
      {p.can_write[name] && p.status === 'PREPARING'
        ? <textarea rows={3} style={{ width: '100%' }} value={inputs[name] || ''} onChange={(e) => setInputs({ ...inputs, [name]: e.target.value })} />
        : <div style={{ whiteSpace: 'pre-wrap' }}>{p.inputs[name] || <span style={muted}>Not added yet.</span>}</div>}
    </div>
  );
  const editable = Object.entries(p.can_write).filter(([, v]) => v).map(([f]) => f);
  return (
    <div className="cv-subsection quarterly-pack">
      <style>{`@media print {
        body * { visibility: hidden; }
        .quarterly-pack, .quarterly-pack * { visibility: visible; }
        .quarterly-pack { position: absolute; left: 0; top: 0; width: 100%; border: none; }
        .quarterly-pack button, .quarterly-pack select, .quarterly-pack input { display: none; }
      }`}</style>
      <div className="cv-subsection-title"><span>{p.label} review · {fmtDate(p.review_date)} · covers {p.covers}</span>
        <button className="btn-secondary" onClick={() => window.print()}><FiPrinter /> Print the pack</button></div>
      <Err text={err} /><Ok text={ok} />

      <div style={{ marginTop: 6 }}><strong>Before the review</strong>
        {p.preparation.map((x) => <div key={x.what} style={row}>{x.what} · <span style={muted}>{x.who} · due {fmtDate(x.due)}</span></div>)}
      </div>

      <div style={{ marginTop: 10 }}><strong>Agenda (Saturday 18:00–21:00 CAT, online)</strong>
        {p.agenda.map((a) => <div key={a.time} style={row}>{a.time} · {a.item}{a.lead ? <span style={muted}> · {a.lead}</span> : null}</div>)}
      </div>

      {field('recognition', 'Recognition and shout-outs')}
      {field('finance_summary', 'Finance summary for the quarter (CFO)')}

      <div style={{ marginTop: 10 }}><strong>KPIs · {k.months.join(' / ')}</strong> <span style={muted}>{k.red_count} KPI row(s) red at least once. Was the cause the person, the process or the target?</span>
        {k.rows.map((r, i) => (
          <div key={i} style={{ ...row, display: 'grid', gridTemplateColumns: '60px 1fr 90px 80px', gap: 6 }}>
            <strong>{r.code}</strong><span>{r.name}{r.person ? ` · ${r.person}` : ''}</span><span><Pills colours={r.colours} /></span><span>{r.value != null ? Number(r.value) : ''}</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 10 }}><strong>People</strong>
        {p.gap ? (
          <>
            <div>Gap analysis ({p.gap.round}): sheets <Counts obj={p.gap.sheets_by_status} /></div>
            <div>Development actions: <Counts obj={p.gap.actions_by_status} /> · overdue {p.gap.actions_overdue}</div>
            {p.gap.gaps_shared_by_several.length > 0 && <div>Gaps shared by more than one person (train as a group?): {p.gap.gaps_shared_by_several.join(' · ')}</div>}
          </>
        ) : <div style={muted}>No gap-analysis round yet.</div>}
        <div>Hiring: {p.hiring.vacancies_open} vacancies open · {p.hiring.applicants} applicants · {p.hiring.hires} hired{p.hiring.avg_days_to_hire != null ? ` · ${p.hiring.avg_days_to_hire} days from advert to hire` : ''} · {p.hiring.values_rejections} stopped at the values gate · {p.hiring.replies_late} applicants without a reply</div>
        <div>Turnover: {p.hiring.leavers} leaver(s){p.hiring.turnover_pct != null ? ` (${p.hiring.turnover_pct}%)` : ''} · <Counts obj={p.hiring.exit_reasons} /></div>
        {p.hiring.exit_themes.length > 0 && <div>Exit-interview themes: {p.hiring.exit_themes.join(' · ')}</div>}
        {field('hiring_note', 'Hiring and turnover update (COO)')}
      </div>

      <div style={{ marginTop: 10 }}><strong>Foundation check</strong>
        <div>Conduct reported by level (no names): <Counts obj={p.conduct.reported_by_level} /> · decided: <Counts obj={p.conduct.decided_by_step} /> · no breach: {p.conduct.no_breach}</div>
        {p.conduct.most_broken.length > 0 && <div>Rules broken most: {p.conduct.most_broken.map((m) => `${m.section}. ${m.name} (${m.count})`).join(' · ')}</div>}
        <div>Complaints: {p.complaints.received} received ({p.complaints.serious} serious) · {p.complaints.closed_on_time} of {p.complaints.closed} closed within 10 working days · {p.complaints.still_open} still open · root causes <Counts obj={p.complaints.by_root_cause} /></div>
      </div>

      <div style={{ marginTop: 10 }}><strong>Settling (CSOs)</strong>
        {p.settling.cities.map((c) => <div key={c.city} style={row}>{c.city}: {c.arrivals} arrival(s){c.avg_days_to_settle != null ? ` · ${c.avg_days_to_settle} days to settle` : ''} · {c.escalations} escalation(s)</div>)}
        <div>Welfare check-ins: {p.settling.check_ins_on_time} of {p.settling.check_ins_due} logged on time · {p.settling.check_ins_urgent} urgent</div>
        {field('cso_reports', 'CSO report per city (COO)')}
      </div>

      {editable.length > 0 && p.status === 'PREPARING' && (
        <div style={{ textAlign: 'right', marginTop: 6 }}>
          <button className="btn-primary" onClick={() => run(() => foundationAPI.reviewAction(p.id, 'inputs', Object.fromEntries(editable.map((f) => [f, inputs[f] || '']))), 'Saved.')}>Save my parts of the pack</button>
        </div>
      )}

      <div style={{ marginTop: 10 }}><strong>Proposed changes</strong>
        {p.proposals.length === 0 && <div style={muted}>None yet. Anyone can propose a change to the rules, RACI, KPIs or a process.</div>}
        {p.proposals.map((x) => (
          <div key={x.id} style={row}>
            <strong>{x.document}:</strong> {x.proposal} <span style={muted}>· {x.by} · {x.outcome_label}{x.outcome_note ? ` (${x.outcome_note})` : ''}</span>
            {p.can_run && x.outcome === 'PENDING' && (
              <span style={{ marginLeft: 8 }}>
                <button className="btn-secondary" onClick={() => run(() => foundationAPI.decideProposal(x.id, { outcome: 'AGREED' }), 'Recorded.')}>Agreed</button>{' '}
                <button className="btn-secondary" onClick={() => run(() => foundationAPI.decideProposal(x.id, { outcome: 'DECLINED' }), 'Recorded.')}>Not agreed</button>
              </span>
            )}
          </div>
        ))}
        {p.status === 'PREPARING' && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <select value={prop.document} onChange={(e) => setProp({ ...prop, document: e.target.value })}>{documents.map(([d, l]) => <option key={d} value={d}>{l}</option>)}</select>
            <input style={{ flex: 1, minWidth: 220 }} placeholder="The change, and why" value={prop.proposal} onChange={(e) => setProp({ ...prop, proposal: e.target.value })} />
            <button className="btn-secondary" disabled={prop.proposal.trim().length < 10} onClick={() => run(() => foundationAPI.reviewAction(p.id, 'propose', prop), 'Proposed.').then((r) => r && setProp({ ...prop, proposal: '' }))}>Propose</button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 10 }}><strong>Questions to ask every quarter</strong>
        <ol style={{ margin: '4px 0 0 18px', fontSize: 13 }}>{p.questions.map((q) => <li key={q}>{q}</li>)}</ol>
      </div>

      {field('decisions', 'Decisions')}
      {p.status === 'HELD' ? (
        <div style={{ marginTop: 10 }}><strong>Next quarter's priorities (held {fmtDate(p.held_on)})</strong>
          {p.priorities.map((x) => <div key={x.task} style={row}>{x.text} · <span style={muted}>owner {x.owner_name} · Team Tasks</span></div>)}
        </div>
      ) : p.can_run && (
        <div style={{ marginTop: 10 }}>
          <strong>After the review: top 3 priorities</strong>
          <div style={muted}>Each becomes a team task with its owner Accountable, due at the end of next quarter.</div>
          {close.priorities.map((x, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <input style={{ flex: 1 }} placeholder={`Priority ${i + 1}`} value={x.text} onChange={(e) => setClose({ ...close, priorities: close.priorities.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)) })} />
              <select value={x.owner} onChange={(e) => setClose({ ...close, priorities: close.priorities.map((y, j) => (j === i ? { ...y, owner: e.target.value } : y)) })}>
                <option value="">Owner…</option>{people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
            <label style={muted}>Held on <input type="date" value={close.held_on} onChange={(e) => setClose({ ...close, held_on: e.target.value })} /></label>
            <button className="btn-primary" onClick={() => window.confirm('Close the review and create the priority tasks?') && run(() => foundationAPI.reviewAction(p.id, 'close', { ...close, decisions: inputs.decisions }), 'Review closed. Priorities are in Team Tasks.')}>Close the review</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ReviewTab = ({ people }) => {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [f, setF] = useState({ year: 2026, quarter: 4 });
  const load = useCallback(() => foundationAPI.reviews().then((r) => setData(r.data)), []);
  const { err, ok, run } = useRun(load);
  useEffect(() => { load().catch(() => {}); }, [load]);
  if (!data) return <Err text={err} />;
  return (
    <div>
      <div style={{ ...muted, marginBottom: 8 }}>Every quarter the whole team reviews the foundation: rules, roles, KPIs, gap analyses, hiring and consequences. Held on the second Saturday after the quarter ends; the pack is built from the VMS.</div>
      <Err text={err} /><Ok text={ok} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {data.reviews.map((r) => <button key={r.id} className={openId === r.id ? 'btn-primary' : 'btn-secondary'} onClick={() => setOpenId(r.id)}>{r.label} · {fmtDate(r.review_date)}{r.status === 'HELD' ? ' · held' : ''}</button>)}
      </div>
      {data.can_open && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
          <select value={f.quarter} onChange={(e) => setF({ ...f, quarter: Number(e.target.value) })}>{[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}</select>
          <input style={{ width: 80 }} type="number" value={f.year} onChange={(e) => setF({ ...f, year: Number(e.target.value) })} />
          <button className="btn-secondary" onClick={() => run(() => foundationAPI.openReview(f), 'Review opened.')}>Open review</button>
        </div>
      )}
      {openId && <Pack key={openId} id={openId} people={people} documents={data.documents} />}
    </div>
  );
};
